import React from "react";
import axios from "axios";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Checkbox from "@mui/material/Checkbox";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDPagination from "components/MDPagination";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";

function Reports() {
  const [users, setUsers] = React.useState([]);
  const [tagOptions, setTagOptions] = React.useState([]);
  const [selectedUserId, setSelectedUserId] = React.useState("");
  const [selectedTagId, setSelectedTagId] = React.useState("");
  const [from, setFrom] = React.useState("2026-03-01");
  const [to, setTo] = React.useState("2026-03-25");
  const [activity, setActivity] = React.useState("Active");
  const [reportSearchInput, setReportSearchInput] = React.useState("");
  const [reportSearchTerm, setReportSearchTerm] = React.useState("");
  const [generatedReports, setGeneratedReports] = React.useState([]);
  const [reportsPage, setReportsPage] = React.useState(1);
  const [reportsLimit] = React.useState(10);
  const [totalReportsCount, setTotalReportsCount] = React.useState(0);
  const [totalReportsPages, setTotalReportsPages] = React.useState(1);
  const [reportsReloadKey, setReportsReloadKey] = React.useState(0);
  const [latestBulkReportId, setLatestBulkReportId] = React.useState("");
  const [matchedUsersCount, setMatchedUsersCount] = React.useState(0);
  const [selectedReportIds, setSelectedReportIds] = React.useState([]);
  const [tagReportTitleDialog, setTagReportTitleDialog] = React.useState({
    open: false,
    title: "",
  });
  const [reportActionAnchorEl, setReportActionAnchorEl] = React.useState(null);
  const [selectedActionReport, setSelectedActionReport] = React.useState(null);
  const [editReportDialog, setEditReportDialog] = React.useState({
    open: false,
    id: "",
    title: "",
  });
  const [pdfDownloadingId, setPdfDownloadingId] = React.useState(null);

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      setReportSearchTerm(reportSearchInput.trim());
    }, 300);
    return () => clearTimeout(timeout);
  }, [reportSearchInput]);

  React.useEffect(() => {
    const fetchReportUsers = async () => {
      const token =
        localStorage.getItem("dsf_token") ||
        localStorage.getItem("token") ||
        localStorage.getItem("accessToken") ||
        "";
      const apiBaseUrl = (
        process.env.REACT_APP_API_BASE_URL || "https://dsf-api.classicprogrammers.com/api/v1/admin"
      ).replace(/\/+$/, "");
      const endpoint = "/reports/users?page=1&limit=10";
      const requestUrls = [];
      if (apiBaseUrl) requestUrls.push(`${apiBaseUrl}${endpoint}`);

      try {
        let response;
        for (const url of requestUrls) {
          try {
            response = await axios.get(url, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            break;
          } catch (_requestError) {
            // Try next URL shape.
          }
        }

        const payload = response?.data?.data || response?.data || {};
        const list = Array.isArray(payload.users)
          ? payload.users
          : Array.isArray(payload.docs)
          ? payload.docs
          : Array.isArray(payload.items)
          ? payload.items
          : [];
        const normalizedUsers = list.map((user, idx) => ({
          id: user?._id || user?.id || `u-${idx}`,
          name: user?.name || "User",
          email: user?.email || "—",
          tags: Array.isArray(user?.tags)
            ? user.tags
                .map((t) => (typeof t === "string" ? t : t?.name || t?.description || ""))
                .filter(Boolean)
            : [],
          activity: user?.activity || (user?.isActive ? "Active" : "Inactive"),
          journals: Number(user?.journals || 0),
          progress: Number(user?.progress || 0),
        }));

        setUsers(normalizedUsers);
        if (normalizedUsers.length) setSelectedUserId(normalizedUsers[0].id);
      } catch (_error) {
        setUsers([]);
        setSelectedUserId("");
      }
    };

    fetchReportUsers();
  }, []);

  React.useEffect(() => {
    const fetchGeneratedReports = async () => {
      const token =
        localStorage.getItem("dsf_token") ||
        localStorage.getItem("token") ||
        localStorage.getItem("accessToken") ||
        "";
      const apiBaseUrl = (
        process.env.REACT_APP_API_BASE_URL || "https://dsf-api.classicprogrammers.com/api/v1/admin"
      ).replace(/\/+$/, "");
      const endpoint = `/reports?page=${reportsPage}&limit=${reportsLimit}${
        reportSearchTerm ? `&search=${encodeURIComponent(reportSearchTerm)}` : ""
      }`;
      const requestUrls = [];
      if (apiBaseUrl) requestUrls.push(`${apiBaseUrl}${endpoint}`);

      try {
        let response;
        for (const url of requestUrls) {
          try {
            response = await axios.get(url, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            break;
          } catch (_requestError) {
            // Try next URL shape.
          }
        }

        const payload = response?.data?.data || response?.data || {};
        const list = Array.isArray(payload.reports)
          ? payload.reports
          : Array.isArray(payload.docs)
          ? payload.docs
          : Array.isArray(payload.items)
          ? payload.items
          : [];
        const pagination = payload.pagination || payload.meta || {};
        const resolvedTotal =
          Number(pagination.total || payload.total || payload.count || list.length) || 0;
        const resolvedTotalPages =
          Number(
            pagination.totalPages ||
              payload.totalPages ||
              (resolvedTotal > 0 ? Math.ceil(resolvedTotal / reportsLimit) : 1)
          ) || 1;

        const normalizedReports = list.map((report, idx) => ({
          id: report?._id || report?.id || `r-${idx}`,
          report: report?.title || report?.report || "Grouped users report",
          scope: report?.scope || report?.type || "tag_bulk",
          created: report?.createdAt
            ? new Intl.DateTimeFormat("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              }).format(new Date(report.createdAt))
            : report?.created || "—",
          users: Array.isArray(report?.users) ? report.users : [],
        }));

        setGeneratedReports(normalizedReports);
        setTotalReportsCount(resolvedTotal);
        setTotalReportsPages(resolvedTotalPages);
      } catch (_error) {
        setGeneratedReports([]);
        setTotalReportsCount(0);
        setTotalReportsPages(1);
      }
    };

    fetchGeneratedReports();
  }, [reportSearchTerm, reportsLimit, reportsPage, reportsReloadKey]);

  React.useEffect(() => {
    const fetchReportTags = async () => {
      const token =
        localStorage.getItem("dsf_token") ||
        localStorage.getItem("token") ||
        localStorage.getItem("accessToken") ||
        "";
      const apiBaseUrl = (
        process.env.REACT_APP_API_BASE_URL || "https://dsf-api.classicprogrammers.com/api/v1/admin"
      ).replace(/\/+$/, "");
      const endpoint = "/tags?page=1&limit=10";
      const requestUrls = [];
      if (apiBaseUrl) requestUrls.push(`${apiBaseUrl}${endpoint}`);

      try {
        let response;
        for (const url of requestUrls) {
          try {
            response = await axios.get(url, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            break;
          } catch (_requestError) {
            // Try next URL shape.
          }
        }

        const payload = response?.data?.data || response?.data || {};
        const list = Array.isArray(payload.tags)
          ? payload.tags
          : Array.isArray(payload.docs)
          ? payload.docs
          : Array.isArray(payload.items)
          ? payload.items
          : [];
        const normalizedTags = list
          .map((tagItem, idx) => {
            if (typeof tagItem === "string") return { id: `tag-${idx}`, name: tagItem };
            const name = tagItem?.name || tagItem?.tag || tagItem?.label || "";
            if (!name) return null;
            return { id: tagItem?._id || tagItem?.id || `tag-${idx}`, name };
          })
          .filter(Boolean);

        setTagOptions(normalizedTags);
        if (normalizedTags.length) setSelectedTagId(normalizedTags[0].id);
      } catch (_error) {
        setTagOptions([]);
        setSelectedTagId("");
      }
    };

    fetchReportTags();
  }, []);

  const selectedUser = React.useMemo(
    () =>
      users.find((user) => user.id === selectedUserId) || {
        id: "",
        name: "User",
        email: "—",
        tags: [],
        activity: "Inactive",
        journals: 0,
        progress: 0,
      },
    [selectedUserId, users]
  );

  const selectedTag = React.useMemo(
    () => tagOptions.find((option) => option.id === selectedTagId) || null,
    [selectedTagId, tagOptions]
  );

  React.useEffect(() => {
    const fetchMatchedUsersCount = async () => {
      if (!selectedTagId) {
        setMatchedUsersCount(0);
        return;
      }
      const token =
        localStorage.getItem("dsf_token") ||
        localStorage.getItem("token") ||
        localStorage.getItem("accessToken") ||
        "";
      const apiBaseUrl = (
        process.env.REACT_APP_API_BASE_URL || "https://dsf-api.classicprogrammers.com/api/v1/admin"
      ).replace(/\/+$/, "");
      const userStatus = String(activity || "all").toLowerCase();
      const endpoint = `/reports/tags/${encodeURIComponent(
        selectedTagId
      )}/matched-users/count?userStatus=${encodeURIComponent(userStatus)}`;
      const requestUrls = [];
      if (apiBaseUrl) requestUrls.push(`${apiBaseUrl}${endpoint}`);
      const noUsersMessage = "No users found for this tag";

      try {
        let response;
        for (const url of requestUrls) {
          try {
            response = await axios.get(url, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            break;
          } catch (requestError) {
            const requestMessage =
              requestError?.response?.data?.message || requestError?.response?.data?.data?.message;
            if (requestMessage === noUsersMessage) {
              toast.info(noUsersMessage);
              setMatchedUsersCount(0);
              return;
            }
            // Try next URL shape.
          }
        }

        const apiMessage = response?.data?.message || response?.data?.data?.message || "";
        if (apiMessage === noUsersMessage) {
          toast.info(apiMessage);
          setMatchedUsersCount(0);
          return;
        }

        const payload = response?.data?.data || response?.data || {};
        const nestedPayload = payload?.data || payload?.result || {};
        const possibleCounts = [
          response?.data?.matchedUsers,
          payload?.matchedUsersCount,
          payload?.matchedUserCount,
          payload?.totalMatchedUsers,
          payload?.matchedUsers,
          payload?.count,
          payload?.total,
          payload?.meta?.total,
          payload?.pagination?.total,
          nestedPayload?.matchedUsersCount,
          nestedPayload?.matchedUserCount,
          nestedPayload?.totalMatchedUsers,
          nestedPayload?.count,
          nestedPayload?.total,
          Array.isArray(payload?.users) ? payload.users.length : undefined,
          Array.isArray(payload?.matchedUsers)
            ? payload.matchedUsers.length
            : payload?.matchedUsers,
          Array.isArray(nestedPayload?.users) ? nestedPayload.users.length : undefined,
          Array.isArray(nestedPayload?.matchedUsers)
            ? nestedPayload.matchedUsers.length
            : nestedPayload?.matchedUsers,
        ];
        const resolvedCountCandidate = possibleCounts.find((value) =>
          Number.isFinite(Number(value))
        );
        const resolvedCount = Number(resolvedCountCandidate ?? 0);
        setMatchedUsersCount(Number.isFinite(resolvedCount) ? resolvedCount : 0);
      } catch (_error) {
        const apiMessage = _error?.response?.data?.message || _error?.response?.data?.data?.message;
        if (apiMessage === noUsersMessage) {
          toast.info(noUsersMessage);
        }
        setMatchedUsersCount(0);
      }
    };

    fetchMatchedUsersCount();
  }, [activity, selectedTagId]);

  const bulkUsers = React.useMemo(
    () =>
      users.filter(
        (user) =>
          user.tags.includes(selectedTag?.name || "") &&
          (activity === "All" || user.activity === activity)
      ),
    [activity, selectedTag?.name, users]
  );

  const triggerDownload = (filename, content, type = "text/plain") => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const triggerBlobDownload = (filename, blob, type = "application/pdf") => {
    const fileBlob = blob instanceof Blob ? blob : new Blob([blob], { type });
    const url = URL.createObjectURL(fileBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const downloadIndividualReport = async (format) => {
    const stamp = new Date().toISOString().slice(0, 10);
    const content = [
      "Individual User Report",
      `Generated: ${new Date().toLocaleString()}`,
      `Name: ${selectedUser.name}`,
      `Email: ${selectedUser.email}`,
      `Tags: ${selectedUser.tags.join(", ")}`,
      `Activity: ${selectedUser.activity}`,
      `Journals: ${selectedUser.journals}`,
      `Progress: ${selectedUser.progress}%`,
      `Range: ${from} to ${to}`,
    ].join("\n");

    if (format === "pdf") {
      setPdfDownloadingId("individual");
      try {
        triggerDownload(`${selectedUser.name.replace(/\s+/g, "-")}-report-${stamp}.txt`, content);
      } finally {
        setPdfDownloadingId(null);
      }
      return;
    }

    const csv = [
      "name,email,tags,activity,journals,progress,from,to",
      `${selectedUser.name},${selectedUser.email},"${selectedUser.tags.join("|")}",${
        selectedUser.activity
      },${selectedUser.journals},${selectedUser.progress},${from},${to}`,
    ].join("\n");
    triggerDownload(
      `${selectedUser.name.replace(/\s+/g, "-")}-report-${stamp}.csv`,
      csv,
      "text/csv"
    );
  };

  const callGenerateReportApi = async (payload) => {
    const token =
      localStorage.getItem("dsf_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken") ||
      "";
    const apiBaseUrl = (
      process.env.REACT_APP_API_BASE_URL || "https://dsf-api.classicprogrammers.com/api/v1/admin"
    ).replace(/\/+$/, "");
    const endpoint = "/reports/generate";
    const requestUrls = [];
    if (apiBaseUrl) requestUrls.push(`${apiBaseUrl}${endpoint}`);

    for (const url of requestUrls) {
      try {
        await axios.post(url, payload, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        return;
      } catch (_requestError) {
        // Try next URL shape.
      }
    }
  };

  const downloadUserReport = async (format) => {
    if (!selectedUser.id) return;
    if (format === "pdf") setPdfDownloadingId(`user-${selectedUser.id}`);
    const token =
      localStorage.getItem("dsf_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken") ||
      "";
    const apiBaseUrl = (
      process.env.REACT_APP_API_BASE_URL || "https://dsf-api.classicprogrammers.com/api/v1/admin"
    ).replace(/\/+$/, "");
    const endpoint = `/reports/users/${selectedUser.id}/download/${format}`;
    const requestUrls = [];
    if (apiBaseUrl) requestUrls.push(`${apiBaseUrl}${endpoint}`);

    for (const url of requestUrls) {
      try {
        const response = await axios.get(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          responseType: format === "pdf" ? "blob" : "blob",
        });

        if (format === "pdf") {
          try {
            triggerBlobDownload(
              `${selectedUser.name || "user"}-report.pdf`,
              response.data,
              "application/pdf"
            );
          } finally {
            setPdfDownloadingId(null);
          }
          return;
        }

        const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
        const fileUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = fileUrl;
        link.download = `${selectedUser.name || "user"}-report.${format}`;
        link.click();
        URL.revokeObjectURL(fileUrl);
        return;
      } catch (_requestError) {
        if (format === "pdf") setPdfDownloadingId(null);
        // Try next URL shape.
      }
    }

    if (format === "pdf") setPdfDownloadingId(null);
  };

  React.useEffect(() => {
    if (!selectedUserId) return;
    callGenerateReportApi({
      title: `${selectedUser.name || "User"} user report`,
      scope: "user",
      userId: selectedUser.id || undefined,
      userStatus: "all",
      dateFrom: from || undefined,
      dateTo: to || undefined,
    });
  }, [from, selectedUser.id, selectedUser.name, selectedUserId, to]);

  const generateBulkReport = async (title) => {
    if (!selectedTagId) return;
    const token =
      localStorage.getItem("dsf_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken") ||
      "";
    const apiBaseUrl = (
      process.env.REACT_APP_API_BASE_URL || "https://dsf-api.classicprogrammers.com/api/v1/admin"
    ).replace(/\/+$/, "");
    const endpoint = "/reports/generate/tags";
    const requestUrls = [];
    if (apiBaseUrl) requestUrls.push(`${apiBaseUrl}${endpoint}`);
    const userStatus = String(activity || "all").toLowerCase();
    const payload = {
      tagId: [selectedTagId],
      title,
      userStatus,
      dateFrom: from || undefined,
      dateTo: to || undefined,
    };

    for (const url of requestUrls) {
      try {
        const response = await axios.post(url, payload, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const responsePayload = response?.data?.data || response?.data || {};
        const createdReportId =
          responsePayload?.report?._id ||
          responsePayload?.report?.id ||
          responsePayload?._id ||
          responsePayload?.id ||
          "";
        if (createdReportId) setLatestBulkReportId(createdReportId);
        toast.success(response?.data?.message || "Tag report generated.");
        setReportsReloadKey((prev) => prev + 1);
        break;
      } catch (_requestError) {
        // Try next URL shape.
      }
    }
  };

  const downloadBulkReport = async (format, usersInScope = bulkUsers) => {
    const stamp = new Date().toISOString().slice(0, 10);
    const title = `bulk-${(selectedTag?.name || "tag")
      .toLowerCase()
      .replace(/\s+/g, "-")}-${stamp}`;

    if (format === "pdf") {
      setPdfDownloadingId("bulk");
      try {
        const body = [
          "Bulk User Report",
          `Generated: ${new Date().toLocaleString()}`,
          `Tag: ${selectedTag?.name || "N/A"}`,
          `Activity: ${activity}`,
          `Range: ${from} to ${to}`,
          `Total Users: ${usersInScope.length}`,
          `Avg Journals: ${
            usersInScope.length
              ? (
                  usersInScope.reduce((acc, user) => acc + user.journals, 0) / usersInScope.length
                ).toFixed(1)
              : 0
          }`,
          `Avg Progress: ${
            usersInScope.length
              ? (
                  usersInScope.reduce((acc, user) => acc + user.progress, 0) / usersInScope.length
                ).toFixed(1)
              : 0
          }%`,
          "",
          ...usersInScope.map(
            (user) =>
              `${user.name} (${user.email}) - ${user.tags.join(", ")} | journals: ${
                user.journals
              } | progress: ${user.progress}%`
          ),
        ].join("\n");
        triggerDownload(`${title}.txt`, body);
      } finally {
        setPdfDownloadingId(null);
      }
      return;
    }

    const csv = [
      "name,email,activity,journals,progress,tags",
      ...usersInScope.map(
        (user) =>
          `${user.name},${user.email},${user.activity},${user.journals},${
            user.progress
          },"${user.tags.join("|")}"`
      ),
    ].join("\n");
    triggerDownload(`${title}.csv`, csv, "text/csv");
  };

  const downloadReportPdf = async (reportId) => {
    if (!reportId || pdfDownloadingId) return;
    setPdfDownloadingId(reportId);
    const token =
      localStorage.getItem("dsf_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken") ||
      "";
    const apiBaseUrl = (
      process.env.REACT_APP_API_BASE_URL || "https://dsf-api.classicprogrammers.com/api/v1/admin"
    ).replace(/\/+$/, "");
    const endpoint = `/reports/${reportId}/download/pdf`;
    const requestUrls = apiBaseUrl ? [`${apiBaseUrl}${endpoint}`] : [];

    try {
      for (const url of requestUrls) {
        try {
          const response = await axios.get(url, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            responseType: "blob",
          });
          triggerBlobDownload(`report-${reportId}.pdf`, response.data, "application/pdf");
          return;
        } catch (_requestError) {
          // Try next URL shape.
        }
      }
      toast.error("Failed to download PDF.");
    } catch (_error) {
      toast.error("Failed to download PDF.");
    } finally {
      setPdfDownloadingId(null);
    }
  };

  const downloadReportCsv = async (reportId) => {
    if (!reportId) return;
    const token =
      localStorage.getItem("dsf_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken") ||
      "";
    const apiBaseUrl = (
      process.env.REACT_APP_API_BASE_URL || "https://dsf-api.classicprogrammers.com/api/v1/admin"
    ).replace(/\/+$/, "");
    const endpoint = `/reports/${reportId}/download/csv`;
    const requestUrls = [];
    if (apiBaseUrl) requestUrls.push(`${apiBaseUrl}${endpoint}`);

    for (const url of requestUrls) {
      try {
        const response = await axios.get(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          responseType: "blob",
        });
        const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
        const fileUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = fileUrl;
        link.download = `report-${reportId}.csv`;
        link.click();
        URL.revokeObjectURL(fileUrl);
        return;
      } catch (_requestError) {
        // Try next URL shape.
      }
    }
  };

  const updateReportTitle = async (reportId, title) => {
    const nextTitle = String(title || "").trim();
    if (!reportId || !nextTitle) return;
    const token =
      localStorage.getItem("dsf_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken") ||
      "";
    const apiBaseUrl = (
      process.env.REACT_APP_API_BASE_URL || "https://dsf-api.classicprogrammers.com/api/v1/admin"
    ).replace(/\/+$/, "");
    const endpoint = `/reports/${reportId}`;
    const requestUrls = [];
    if (apiBaseUrl) requestUrls.push(`${apiBaseUrl}${endpoint}`);

    for (const url of requestUrls) {
      try {
        await axios.patch(
          url,
          {
            title: nextTitle,
          },
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );
        setReportsReloadKey((prev) => prev + 1);
        toast.success("Report updated successfully.");
        return;
      } catch (_requestError) {
        // Try next URL shape.
      }
    }
    toast.error("Failed to update report.");
  };

  const deleteSingleReport = async (reportId) => {
    if (!reportId) return;
    if (!window.confirm("Do you want to delete this report?")) return;
    const token =
      localStorage.getItem("dsf_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken") ||
      "";
    const apiBaseUrl = (
      process.env.REACT_APP_API_BASE_URL || "https://dsf-api.classicprogrammers.com/api/v1/admin"
    ).replace(/\/+$/, "");
    const endpoint = `/reports/${reportId}`;
    const requestUrls = [];
    if (apiBaseUrl) requestUrls.push(`${apiBaseUrl}${endpoint}`);

    for (const url of requestUrls) {
      try {
        await axios.delete(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        setSelectedReportIds((prev) => prev.filter((id) => id !== reportId));
        setReportsReloadKey((prev) => prev + 1);
        toast.success("Report deleted successfully.");
        return;
      } catch (_requestError) {
        // Try next URL shape.
      }
    }
    toast.error("Failed to delete report.");
  };

  const openReportPdf = async (reportId) => {
    if (!reportId || pdfDownloadingId) return;
    setPdfDownloadingId(reportId);
    const token =
      localStorage.getItem("dsf_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken") ||
      "";
    const apiBaseUrl = (
      process.env.REACT_APP_API_BASE_URL || "https://dsf-api.classicprogrammers.com/api/v1/admin"
    ).replace(/\/+$/, "");
    if (!apiBaseUrl) {
      toast.error("Failed to open report.");
      setPdfDownloadingId(null);
      return;
    }

    try {
      const response = await axios.get(`${apiBaseUrl}/reports/${reportId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        responseType: "blob",
      });
      const contentType = response.headers["content-type"] || "";
      if (contentType.includes("application/json")) {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        toast.error(errorData?.message || "Failed to open report.");
        return;
      }
      const pdfBlob = new Blob([response.data], { type: "application/pdf" });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, "_blank");
    } catch (_error) {
      toast.error("Failed to open report.");
    } finally {
      setPdfDownloadingId(null);
    }
  };

  const bulkDeleteReports = async () => {
    if (!selectedReportIds.length) return;
    if (!window.confirm("Do you want to delete selected reports?")) return;
    const token =
      localStorage.getItem("dsf_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken") ||
      "";
    const apiBaseUrl = (
      process.env.REACT_APP_API_BASE_URL || "https://dsf-api.classicprogrammers.com/api/v1/admin"
    ).replace(/\/+$/, "");
    const endpoint = "/reports/bulk-delete";
    const requestUrls = [];
    if (apiBaseUrl) requestUrls.push(`${apiBaseUrl}${endpoint}`);

    for (const url of requestUrls) {
      try {
        await axios.delete(url, {
          data: { ids: selectedReportIds },
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        setSelectedReportIds([]);
        setReportsReloadKey((prev) => prev + 1);
        toast.success("Selected reports deleted successfully.");
        return;
      } catch (_requestError) {
        // Try next URL shape.
      }
    }
    toast.error("Failed to delete selected reports.");
  };

  React.useEffect(() => {
    setSelectedReportIds((prev) => prev.filter((id) => generatedReports.some((r) => r.id === id)));
  }, [generatedReports]);

  const allReportsSelected =
    generatedReports.length > 0 && selectedReportIds.length === generatedReports.length;
  const partiallySelected =
    selectedReportIds.length > 0 && selectedReportIds.length < generatedReports.length;
  const resolvedBulkReportId =
    latestBulkReportId ||
    generatedReports.find((report) =>
      String(report.scope || "")
        .toLowerCase()
        .includes("bulk")
    )?.id ||
    generatedReports[0]?.id ||
    "";

  const columns = React.useMemo(
    () => [
      { Header: "select", accessor: "select", align: "center", width: "6%" },
      { Header: "report", accessor: "report", align: "left" },
      { Header: "scope", accessor: "scope", align: "left" },
      { Header: "created", accessor: "created", align: "center" },
      { Header: "action", accessor: "action", align: "center" },
    ],
    []
  );

  const rows = React.useMemo(
    () =>
      generatedReports.map((r) => ({
        select: (
          <MDBox display="flex" justifyContent="center">
            <Checkbox
              size="small"
              checked={selectedReportIds.includes(r.id)}
              onChange={() =>
                setSelectedReportIds((prev) =>
                  prev.includes(r.id) ? prev.filter((id) => id !== r.id) : [...prev, r.id]
                )
              }
            />
          </MDBox>
        ),
        report: (
          <MDTypography variant="button" fontWeight="medium" color="text">
            {r.report}
          </MDTypography>
        ),
        scope: (
          <MDTypography variant="caption" color="text">
            {r.scope}
          </MDTypography>
        ),
        created: (
          <MDTypography variant="caption" color="text">
            {r.created}
          </MDTypography>
        ),
        action: (
          <MDBox display="flex" justifyContent="center">
            <IconButton
              size="small"
              sx={{ color: "#344767" }}
              aria-label="Report actions"
              onClick={(event) => {
                setReportActionAnchorEl(event.currentTarget);
                setSelectedActionReport(r);
              }}
            >
              <Icon fontSize="small">more_vert</Icon>
            </IconButton>
          </MDBox>
        ),
      })),
    [generatedReports, selectedReportIds]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="contained"
                borderRadius="lg"
                coloredShadow="none"
                sx={{ background: "rgba(229, 10, 70, 1)" }}
              >
                <MDTypography variant="h6" color="white">
                  User Reporting
                </MDTypography>
              </MDBox>

              <MDBox p={3}>
                <MDBox mt={0.2} maxWidth={{ xs: "100%", md: "28rem" }}>
                  <MDBox display="flex" gap={1} alignItems="center">
                    <MDInput
                      label="Search reports"
                      fullWidth
                      value={reportSearchInput}
                      onChange={(e) => setReportSearchInput(e.target.value)}
                    />
                    <MDButton
                      variant="gradient"
                      color="info"
                      sx={{
                        background: "rgba(229, 10, 70, 1)",
                        "&:hover": { background: "rgba(229, 10, 70, 0.9)" },
                      }}
                      onClick={() => setReportSearchTerm(reportSearchInput.trim())}
                    >
                      Search
                    </MDButton>
                  </MDBox>
                </MDBox>

                <MDBox mt={4}>
                  <MDTypography variant="button" fontWeight="medium" color="text">
                    Group/Bulk Report by Tags
                  </MDTypography>
                </MDBox>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <MDInput
                      select
                      label="Tag"
                      fullWidth
                      SelectProps={{ native: true }}
                      value={selectedTagId}
                      onChange={(e) => setSelectedTagId(e.target.value)}
                    >
                      {tagOptions.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.name}
                        </option>
                      ))}
                    </MDInput>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <MDInput
                      label="From"
                      type="date"
                      fullWidth
                      value={from}
                      onChange={(e) => setFrom(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <MDInput
                      label="To"
                      type="date"
                      fullWidth
                      value={to}
                      onChange={(e) => setTo(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <MDInput
                      select
                      label="Activity"
                      fullWidth
                      SelectProps={{ native: true }}
                      value={activity}
                      onChange={(e) => setActivity(e.target.value)}
                    >
                      <option value="All">All</option>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </MDInput>
                  </Grid>
                </Grid>
                <MDBox mt={1}>
                  <MDTypography variant="caption" color="text">
                    Matched users: {matchedUsersCount}
                  </MDTypography>
                  <MDTypography variant="caption" color="text" display="block">
                    Exports include activity, journals, and progress fields.
                  </MDTypography>
                </MDBox>

                <MDBox mt={2} display="flex" gap={1} flexWrap="wrap">
                  <MDButton
                    variant="gradient"
                    color="info"
                    sx={{
                      background: "rgba(229, 10, 70, 1)",
                      "&:hover": { background: "rgba(229, 10, 70, 0.9)" },
                    }}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      setTagReportTitleDialog({
                        open: true,
                        title: `${selectedTag?.name || "Tag"} report`,
                      });
                    }}
                  >
                    Generate report
                  </MDButton>
                </MDBox>

                <MDBox mt={3}>
                  <MDBox
                    mb={1}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap={1}
                  >
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <Checkbox
                        size="small"
                        checked={allReportsSelected}
                        indeterminate={partiallySelected}
                        onChange={() =>
                          setSelectedReportIds(
                            allReportsSelected ? [] : generatedReports.map((report) => report.id)
                          )
                        }
                      />
                      <MDTypography variant="caption" color="text">
                        Select all
                      </MDTypography>
                    </MDBox>
                    <MDTypography variant="caption" color="text">
                      Page {reportsPage} of {totalReportsPages} | Limit {reportsLimit} | Total{" "}
                      {totalReportsCount}
                    </MDTypography>
                    <MDButton
                      variant="outlined"
                      color="error"
                      disabled={!selectedReportIds.length}
                      onClick={bulkDeleteReports}
                    >
                      Bulk Delete
                    </MDButton>
                  </MDBox>
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                  <MDBox
                    pt={2}
                    pb={1}
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-end"
                    flexWrap="wrap"
                    gap={1}
                  >
                    <MDPagination
                      variant="gradient"
                      color="dark"
                      sx={{
                        "& .MuiPaginationItem-root": {
                          color: "rgba(229, 10, 70, 1)",
                          borderColor: "rgba(229, 10, 70, 0.35)",
                        },
                        "& .MuiPaginationItem-root.Mui-selected": {
                          backgroundColor: "rgba(229, 10, 70, 1) !important",
                          color: "#fff !important",
                        },
                      }}
                    >
                      <MDPagination
                        item
                        disabled={reportsPage <= 1}
                        onClick={() => setReportsPage((prev) => Math.max(1, prev - 1))}
                      >
                        <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                      </MDPagination>
                      {Array.from({ length: totalReportsPages }, (_, index) => index + 1)
                        .slice(Math.max(0, reportsPage - 3), Math.max(0, reportsPage - 3) + 5)
                        .map((pageNumber) => (
                          <MDPagination
                            key={pageNumber}
                            item
                            active={pageNumber === reportsPage}
                            onClick={() => setReportsPage(pageNumber)}
                          >
                            {pageNumber}
                          </MDPagination>
                        ))}
                      <MDPagination
                        item
                        disabled={reportsPage >= totalReportsPages}
                        onClick={() =>
                          setReportsPage((prev) => Math.min(totalReportsPages, prev + 1))
                        }
                      >
                        <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                      </MDPagination>
                    </MDPagination>
                  </MDBox>
                  <Menu
                    anchorEl={reportActionAnchorEl}
                    open={Boolean(reportActionAnchorEl)}
                    onClose={() => {
                      setReportActionAnchorEl(null);
                      setSelectedActionReport(null);
                    }}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                    transformOrigin={{ vertical: "top", horizontal: "center" }}
                  >
                    <MenuItem
                      onClick={() => {
                        if (!selectedActionReport) return;
                        const reportId = selectedActionReport.id;
                        setReportActionAnchorEl(null);
                        setSelectedActionReport(null);
                        openReportPdf(reportId);
                      }}
                    >
                      View
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        if (!selectedActionReport) return;
                        setEditReportDialog({
                          open: true,
                          id: selectedActionReport.id,
                          title: selectedActionReport.report || "",
                        });
                        setReportActionAnchorEl(null);
                        setSelectedActionReport(null);
                      }}
                    >
                      Edit
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        if (!selectedActionReport) return;
                        deleteSingleReport(selectedActionReport.id);
                        setReportActionAnchorEl(null);
                        setSelectedActionReport(null);
                      }}
                      sx={{ color: "error.main" }}
                    >
                      Delete
                    </MenuItem>
                    <MenuItem
                      disabled={Boolean(pdfDownloadingId)}
                      onClick={() => {
                        if (!selectedActionReport || pdfDownloadingId) return;
                        const reportId = selectedActionReport.id;
                        setReportActionAnchorEl(null);
                        setSelectedActionReport(null);
                        downloadReportPdf(reportId);
                      }}
                    >
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <Icon fontSize="small" sx={{ color: "rgba(229, 10, 70, 1)" }}>
                          picture_as_pdf
                        </Icon>
                        PDF
                      </MDBox>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        if (!selectedActionReport) return;
                        downloadReportCsv(selectedActionReport.id);
                        setReportActionAnchorEl(null);
                        setSelectedActionReport(null);
                      }}
                    >
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <Icon fontSize="small" sx={{ color: "rgba(76, 175, 80, 1)" }}>
                          description
                        </Icon>
                        CSV
                      </MDBox>
                    </MenuItem>
                  </Menu>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Dialog
        open={tagReportTitleDialog.open}
        onClose={() => setTagReportTitleDialog({ open: false, title: "" })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Tag Report Title</DialogTitle>
        <DialogContent>
          <MDBox mt={1}>
            <MDInput
              label="Report title"
              fullWidth
              value={tagReportTitleDialog.title}
              onChange={(event) =>
                setTagReportTitleDialog((prev) => ({ ...prev, title: event.target.value }))
              }
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="text"
            color="dark"
            onClick={() => setTagReportTitleDialog({ open: false, title: "" })}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            onClick={async () => {
              const nextTitle = tagReportTitleDialog.title.trim();
              if (!nextTitle) {
                toast.error("Please enter report title.");
                return;
              }
              await generateBulkReport(nextTitle);
              setTagReportTitleDialog({ open: false, title: "" });
            }}
            sx={{
              background: "rgba(229, 10, 70, 1)",
              "&:hover": { background: "rgba(229, 10, 70, 0.9)" },
            }}
          >
            Generate
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={editReportDialog.open}
        onClose={() => setEditReportDialog({ open: false, id: "", title: "" })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Report</DialogTitle>
        <DialogContent>
          <MDBox mt={1}>
            <MDInput
              label="Report title"
              fullWidth
              value={editReportDialog.title}
              onChange={(event) =>
                setEditReportDialog((prev) => ({ ...prev, title: event.target.value }))
              }
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="text"
            color="dark"
            onClick={() => setEditReportDialog({ open: false, id: "", title: "" })}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            onClick={async () => {
              if (!editReportDialog.id || !editReportDialog.title.trim()) return;
              await updateReportTitle(editReportDialog.id, editReportDialog.title);
              setEditReportDialog({ open: false, id: "", title: "" });
            }}
            sx={{
              background: "rgba(229, 10, 70, 1)",
              "&:hover": { background: "rgba(229, 10, 70, 0.9)" },
            }}
          >
            Save
          </MDButton>
        </DialogActions>
      </Dialog>
      <Backdrop
        open={Boolean(pdfDownloadingId)}
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        <MDBox display="flex" flexDirection="column" alignItems="center" gap={1.5}>
          <CircularProgress color="inherit" size={36} />
          <MDTypography variant="button" color="white" fontWeight="medium">
            Loading report...
          </MDTypography>
        </MDBox>
      </Backdrop>
      <ToastContainer position="top-right" autoClose={3000} />
    </DashboardLayout>
  );
}

export default Reports;
